import { Grid, Typography } from '@mui/material';
import {
  getProperties,
  PermissionsEnum,
  ProcureUser,
  Property,
  USER_ROLES,
} from 'vendor-marketplace-shared-library';
import StoreIcon from '@mui/icons-material/Store';
import React, { Dispatch, SetStateAction, useState } from 'react';
import PrimaryButton from '../../../components/common/styled-mui-overrides/PrimaryButton';
import { DropdownWithCheckboxes } from '../../../components/common/DropdownWithCheckboxes';
import UserService from '../../../services/user.service';
import { UPDATE_PROPERTIES_ON_USER } from '../../../error-messages';
import useAuth from '../../../hooks/useAuth';
import useAlert from '../../../hooks/useAlert';
import { useQuery } from '@apollo/client';

type UserPropertiesModuleProps = {
  user: ProcureUser;
  userProperties: Property[];
  selectedPropertyIds: string[];
  setSelectedPropertyIds: Dispatch<SetStateAction<string[]>>;
  refetchUserData: () => void;
};

export default function UserPropertiesModule(props: UserPropertiesModuleProps) {
  const { validateUserPermissions } = useAuth();
  const alert = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: allPropertyData } = useQuery(getProperties);

  const setPropertiesOnUser = async () => {
    try {
      await UserService.setPropertiesOnUsers(
        Number(props.user.id),
        props.selectedPropertyIds.map(id => Number(id)),
      );
    } catch (e: any) {
      alert.setAlert({ severity: 'error', message: e.message || UPDATE_PROPERTIES_ON_USER });
    }
    props.refetchUserData();
  };

  return (
    <Grid container sx={styles.mainContainer} padding={2} rowGap={2}>
      <Grid item container justifyContent={'space-between'}>
        <Grid item>
          <Typography variant={'h6'}>Properties:</Typography>
        </Grid>
        {validateUserPermissions([PermissionsEnum.PROPERTY_USERS_UPDATE]) &&
          props.user.userRole?.name !== USER_ROLES.ADMIN.name && (
            <Grid item>
              <PrimaryButton
                onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
              >
                Change selection
              </PrimaryButton>
            </Grid>
          )}
      </Grid>
      {props.userProperties.map(property => (
        <Grid item container key={property.id} sx={styles.card}>
          <Grid item>
            <StoreIcon fontSize={'large'} />
          </Grid>
          <Grid item>
            <Grid container direction={'column'}>
              <Grid item>
                <Typography variant={'body2'}>{property.name}</Typography>
              </Grid>
              <Grid item>
                <Typography variant={'caption'}>{property.fullAddress}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ))}
      <DropdownWithCheckboxes
        entities={allPropertyData?.getProperties ?? []}
        onClose={() => {
          setAnchorEl(null);
          setPropertiesOnUser();
        }}
        onSave={() => {}}
        anchorEl={anchorEl}
        selectedIds={props.selectedPropertyIds}
        setSelectedIds={props.setSelectedPropertyIds}
        displayFieldKey={'name'}
      />
    </Grid>
  );
}

const styles = {
  nameContainer: {
    flexWrap: 'nowrap',
    columnGap: 1,
  },
  mainContainer: {
    backgroundColor: 'secondary.light',
    borderRadius: 2,
    rowGap: 2,
  },
  card: {
    alignItems: 'center',
    columnGap: 1,
    backgroundColor: 'white',
    borderRadius: 1,
    padding: 1,
    flexWrap: 'nowrap',
    border: '1px solid black',
  },
};
